import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import HotelStrategyTablesCellsTdNonComputable from 'lumen/components/hotel/strategy/tables/cells/td-non-computable';
import HotelStrategyTablesCellsTdStrategyBaserate from 'lumen/components/hotel/strategy/tables/cells/td-strategy-baserate';
import HotelStrategyTablesCellsTdStrategyHotel from 'lumen/components/hotel/strategy/tables/cells/td-strategy-hotel';
import HotelStrategyTablesCellsThStrategyDetail from 'lumen/components/hotel/strategy/tables/cells/th-strategy-detail';
import {
  COLUMN_CONFIG,
  COLUMN_PROPERTIES,
  COLUMN_WIDTH,
  SUBTYPE_DELIMITER,
  TYPE_ID_BEST_FLEX,
  TYPE_ID_NON_REF,
  TYPE_ID_SEMI_FLEX,
} from 'lumen/utils/constants/rate-strategy-constants';

const VALUE_PATH_HOTEL = COLUMN_PROPERTIES.HOTELINFO;
const VALUE_PATH_BEST_FLEX_CUT_OFF_POLICY = `${TYPE_ID_BEST_FLEX}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.CUT_OFF_POLICY}`;
const VALUE_PATH_BEST_FLEX_RATE = `${TYPE_ID_BEST_FLEX}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.RATE}`;
const VALUE_PATH_SEMI_FLEX_CUT_OFF_POLICY = `${TYPE_ID_SEMI_FLEX}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.CUT_OFF_POLICY}`;
const VALUE_PATH_SEMI_FLEX_RATE = `${TYPE_ID_SEMI_FLEX}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.RATE}`;
const VALUE_PATH_SEMI_FLEX_DISCOUNT = `${TYPE_ID_SEMI_FLEX}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.DISCOUNT}`;
const VALUE_PATH_NON_REF_RATE = `${TYPE_ID_NON_REF}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.RATE}`;
const VALUE_PATH_NON_REF_DISCOUNT = `${TYPE_ID_NON_REF}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.DISCOUNT}`;

interface HotelStrategyTablesTableCancellationPolicySignature {
  Args: {
    initialSorts: unknown;
    onTableSort: unknown;
    sortFunction: unknown;
  };
}

export default class HotelStrategyTablesTableCancellationPolicy extends Component<HotelStrategyTablesTableCancellationPolicySignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const columns = [
      {
        ...COLUMN_CONFIG.HOTELINFO_HEADER,
        subcolumns: [
          {
            ...COLUMN_CONFIG.HOTELINFO,
            name: this.intl.t('general.hotels'),
            tdComponent: HotelStrategyTablesCellsTdStrategyHotel,
            valuePath: VALUE_PATH_HOTEL,
          },
        ],
      },
      {
        name: this.intl.t('rates.best_flex'),
        thComponent: HotelStrategyTablesCellsThStrategyDetail,
        width: 2 * COLUMN_WIDTH.DEFAULT,
        columnKey: TYPE_ID_BEST_FLEX,
        hasRightBorder: true,
        hasCompareButton: true,
        subcolumns: [
          {
            name: this.intl.t('strategy.metric_block.cutoff_policy'),
            valuePath: VALUE_PATH_BEST_FLEX_CUT_OFF_POLICY,
            displayValuePath: `${VALUE_PATH_BEST_FLEX_CUT_OFF_POLICY}.formattedValue`,
            width: COLUMN_WIDTH.DEFAULT,
            textAlign: 'center',
          },
          {
            name: this.intl.t('rates.rate'),
            valuePath: VALUE_PATH_BEST_FLEX_RATE,
            tdComponent: HotelStrategyTablesCellsTdStrategyBaserate,
            width: COLUMN_WIDTH.DEFAULT,
            columnKey: TYPE_ID_BEST_FLEX,
            textAlign: 'center',
            hasRightBorder: true,
          },
        ],
      },
      {
        name: this.intl.t('rates.semi_flex'),
        thComponent: HotelStrategyTablesCellsThStrategyDetail,
        width: 3 * COLUMN_WIDTH.DEFAULT,
        hasRightBorder: true,
        subcolumns: [
          {
            name: this.intl.t('strategy.metric_block.cutoff_policy'),
            valuePath: VALUE_PATH_SEMI_FLEX_CUT_OFF_POLICY,
            displayValuePath: `${VALUE_PATH_SEMI_FLEX_CUT_OFF_POLICY}.formattedValue`,
            width: COLUMN_WIDTH.DEFAULT,
            textAlign: 'center',
          },

          {
            name: this.intl.t('rates.rate'),
            valuePath: VALUE_PATH_SEMI_FLEX_RATE,
            tdComponent: HotelStrategyTablesCellsTdStrategyBaserate,
            width: COLUMN_WIDTH.DEFAULT,
            textAlign: 'center',
            isRedirectDisabled: true, // No redirects from semi-flex column
          },

          {
            name: this.intl.t('strategy.metric_block.discount'),
            valuePath: VALUE_PATH_SEMI_FLEX_DISCOUNT,
            tdComponent: HotelStrategyTablesCellsTdNonComputable,
            width: COLUMN_WIDTH.DEFAULT,
            textAlign: 'center',
            hasRightBorder: true,
          },
        ],
      },
      {
        name: this.intl.t('rates.nonrefundable'),
        columnKey: TYPE_ID_NON_REF,
        thComponent: HotelStrategyTablesCellsThStrategyDetail,
        width: 2 * COLUMN_WIDTH.DEFAULT,
        hasRightBorder: true,
        hasCompareButton: true,
        subcolumns: [
          {
            name: this.intl.t('rates.rate'),
            valuePath: VALUE_PATH_NON_REF_RATE,
            tdComponent: HotelStrategyTablesCellsTdStrategyBaserate,
            width: COLUMN_WIDTH.DEFAULT,
            columnKey: TYPE_ID_NON_REF,
            textAlign: 'center',
          },
          {
            name: this.intl.t('strategy.metric_block.discount'),
            valuePath: VALUE_PATH_NON_REF_DISCOUNT,
            tdComponent: HotelStrategyTablesCellsTdNonComputable,
            width: COLUMN_WIDTH.DEFAULT,
            textAlign: 'center',
            hasRightBorder: true,
          },
        ],
      },
    ];

    return columns;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    // @ts-expect-error: Incorrect type
    const rows = [];

    const cancellationData = [
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      ...this.args.cancellationPolicyPerHotelInfo.values(),
    ];

    cancellationData.forEach((rowData) => {
      const row = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        [VALUE_PATH_HOTEL]: rowData[VALUE_PATH_HOTEL],

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [VALUE_PATH_BEST_FLEX_CUT_OFF_POLICY]:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rowData[TYPE_ID_BEST_FLEX][COLUMN_PROPERTIES.CUT_OFF_POLICY],

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [VALUE_PATH_BEST_FLEX_RATE]:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rowData[TYPE_ID_BEST_FLEX][COLUMN_PROPERTIES.RATE],

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [VALUE_PATH_SEMI_FLEX_CUT_OFF_POLICY]:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rowData[TYPE_ID_SEMI_FLEX][COLUMN_PROPERTIES.CUT_OFF_POLICY],

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [VALUE_PATH_SEMI_FLEX_RATE]:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rowData[TYPE_ID_SEMI_FLEX][COLUMN_PROPERTIES.RATE],

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [VALUE_PATH_SEMI_FLEX_DISCOUNT]:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rowData[TYPE_ID_SEMI_FLEX][COLUMN_PROPERTIES.DISCOUNT],

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [VALUE_PATH_NON_REF_RATE]:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rowData[TYPE_ID_NON_REF][COLUMN_PROPERTIES.RATE],

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [VALUE_PATH_NON_REF_DISCOUNT]:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rowData[TYPE_ID_NON_REF][COLUMN_PROPERTIES.DISCOUNT],
      };

      rows.push(row);
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return rows;
  }

  <template>
    <PrismTable
      {{! @glint-expect-error: Incorrect type }}
      @columns={{this.columns}}
      @enableAutoSortsUpdate={{true}}
      @fillMode="first-column"
      {{! @glint-expect-error: Incorrect type }}
      @initialSorts={{@initialSorts}}
      {{! @glint-expect-error: Incorrect type }}
      @onTableSort={{@onTableSort}}
      @removeTableBorder={{true}}
      @rows={{this.rows}}
      {{! @glint-expect-error: Incorrect type }}
      @sortFunction={{@sortFunction}}
      @widthConstraint="eq-container-slack"
    />
  </template>
}
